<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { RestrictionsFeaturesEnum } from "@/__generated__/types";
import OgRestrictionsLanding from "@/components/organisms/OgRestrictionsLanding.vue";
import useRestrictions from "@/utils/composables/useRestrictions/useRestrictions";

const CARBON_ACCOUNTING = "carbon_accounting";

const route = useRoute();
const { t, locale } = useI18n();

const { restrictions, loading: restrictionsLoading } = useRestrictions();

const esrsPrefixes = [
	"e1",
	"e2",
	"e3",
	"e4",
	"e5",
	"s1",
	"s2",
	"s3",
	"s4",
	"g1",
	"esrs_2",
];

const showCarbonAccountingInfo = computed(() => {
	return (
		route.query.category === CARBON_ACCOUNTING &&
		restrictions.value?.restrictedFeatures.includes(
			RestrictionsFeaturesEnum.CARBON_ACCOUNTING,
		) &&
		!restrictionsLoading.value
	);
});

const showESRSInfo = computed(() => {
	return (
		esrsPrefixes.some((p) =>
			String(route.query.category).startsWith(`${p}_`),
		) &&
		restrictions.value?.restrictedFeatures.includes(
			RestrictionsFeaturesEnum.ESRS,
		) &&
		!restrictionsLoading.value
	);
});
</script>

<template>
  <OgRestrictionsLanding v-if="showCarbonAccountingInfo">
    <template #pageTitle>
      {{ t('Carbon accounting') }}
    </template>
    <template #title>
      {{ t('Assess and manage carbon emissions') }}
    </template>
    <template #text>
      {{
        t('Evaluate your contribution to the global greenhouse gas emissions.')
      }}<br />
      {{ t('Calculate your carbon footprint for scope 1, 2 and 3.') }}
    </template>
    <template #image>
      <img
        v-if="locale === 'de'"
        src="@/assets/images/carbon-accounting-de.png"
        alt="Treibhausgasbilanz"
      />
      <img
        v-else
        alt="Carbon accounting"
        src="@/assets/images/carbon-accounting.png"
      />
    </template>
  </OgRestrictionsLanding>
  <OgRestrictionsLanding v-if="showESRSInfo">
    <template #pageTitle>
      {{ t('European Sustainability Reporting Standard (ESRS)') }}
    </template>
    <template #title>
      {{ t('Collect and manage ESRS data points for the CSRD') }}<br />
    </template>
    <template #text>
      {{t('Assign ESRS topics, collect data continuously and use our AI to optimize your responses.')}}<br />
      {{t('Get your CSRD reporting done faster.')}}
    </template>
    <template #image>
      <img
          v-if="locale === 'de'"
          src="@/assets/images/esrs-restriction-de.png"
          alt="Treibhausgasbilanz"
      />
      <img
          v-else
          alt="Carbon accounting"
          src="@/assets/images/esrs-restriction.png"
      />
    </template>

  </OgRestrictionsLanding>
  <router-view :key="String(route.params.project)" />
</template>
